import  { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../../lib/firebase';
import Header from '../../components/Header';
import { getCookie } from "../../utils/cookieUtil";
import { EyeIcon, EyeOffIcon, InformationCircleIcon } from '@heroicons/react/solid';
import { Tooltip } from 'react-tooltip';
import '@fortawesome/fontawesome-free/css/all.min.css';

const UserDetailsPage = ({ closeModal }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uid, setUid] = useState(null);
  const [permission, setPermission] = useState('');
  const [error, setError] = useState(null);
  const [hovered, setHovered] = useState(null); // Added hovered state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null); // store the selected step


  const modalContent = {
    1: 'After completing another day of the challenge, how motivated are you to start the next day and try to reach your steps goal?',
    2: 'How do you rate your likelihood of reaching your steps goal today?',
    3: 'How committed do you think you are to reaching your steps goal in the next day?',
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uidFromParams = params.get('uid');
    if (uidFromParams) {
      setUid(uidFromParams);
    }
  }, []);

  useEffect(() => {
    const roles = getCookie('roleId');
    if (roles) {
      setPermission(`${roles} `);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!uid) return;
      setLoading(true);
      try {
        const userDoc = await getDoc(doc(firestore, 'Users', uid));
        const user = userDoc.exists() ? userDoc.data() : {};

        const balanceDoc = await getDoc(doc(firestore, 'Cumulative Balance', uid));
        const balance = balanceDoc.exists() ? balanceDoc.data().balance : 0;

        const dailyStepsQuery = query(collection(firestore, 'Daily Steps'), where('userId', '==', uid));
        const dailyStepsSnapshot = await getDocs(dailyStepsQuery);
        const dailySteps = dailyStepsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const feedbackQuery = query(collection(firestore, 'Review'), where('userId', '==', uid));
        const feedbackSnapshot = await getDocs(feedbackQuery);
        const feedback = feedbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log(feedback);


        const freshStartSnapshot = await getDocs(collection(firestore, 'Fresh Start'));
        const freshStart = freshStartSnapshot.docs
          .map(doc => ({
            id: doc.id,
            Group: doc.data().Group,
            dates: doc.data().dates || [],
            freshStart: doc.data().freshStart
          }))
          .filter(doc => doc.id === uid);

        const allDates = freshStart.flatMap(item => item.dates);

        const goalsDoc = await getDoc(doc(firestore, 'Goals', uid));
        const goals = goalsDoc.exists() ? goalsDoc.data() : {};
        const goalDate = goals.goalSettingDate ? goals.goalSettingDate.toDate().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }) : null;

        setUserDetails({ user, balance, dailySteps, freshStart, goals, goalDate, feedback, allDates });
      } catch (error) {
        console.error('Error fetching user details:', error);
        setError('Failed to fetch user details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [uid]);

  const LoadingSpinner = () => (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
      <div className="w-16 h-16 border-t-4 border-[#900028] border-solid rounded-full animate-spin"></div>
    </div>
  );

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">{error}</div>;

  const { user, balance, goals, goalDate, dailySteps, feedback ,allDates  } = userDetails || {};
  const sortedDailySteps = dailySteps?.sort((a, b) => a.day - b.day) || [];
  const sortedFeedback = feedback?.sort((a, b) => a.day - b.day) || [];
  // const allDates = [
  //   { date: { seconds: 1715097600 } },
  //   { date: { seconds: 1715184000 } },
  //   { date: { seconds: 1715270400 } },
  // ];

  
  const handleClose = () => {
    setShowEditModal(false);
  };

  
  


  return (
    <div className=" bg-white-100 flex items-center justify-center">
      <div className=" max-w-xl mt-3  w-full bg-white rounded-2xl shadow-xl px-6 py-2 relative">
        {/* <img src={Watermark} alt="Watermark" className="absolute opacity-10 top-10 right-10 w-24 z-0" /> */}
        <div className="p-3  space-y-4 ">

          {/* User Card */}
          <div className="grid grid-cols-3 items-center bg-white shadow-xl rounded-lg p-3 py-2">
            {/* Left Section: Avatar + Name + Email */}
              {permission.includes("adduser") && (            
            <div className="flex items-center space-x-3 col-span-2">
              <div className="bg-red-800 text-white font-bold w-10 h-10 rounded-full flex items-center justify-center text-base">
                {user?.name?.firstName?.[0]?.toUpperCase() || 'U'}
              </div>
              <div>
                  <div className="font-semibold text-sm">
                  {user?.name?.firstName} {user?.name?.secondName || ''}
                </div>
                <div className="text-xs text-gray-700">{user?.email}</div>
              </div>
            </div>
              )}
            {/* Right Section: Group */}
            <div className="text-left">
              <div className="text-xs font-semibold text-gray-700">Group</div>
              <div className="text-xs">{user?.group || 'N/A'}</div>
            </div>
          </div>


          {/* Goal Card */}
          <div className="bg-white shadow-xl rounded-lg p-3 space-y-1">
            <div className="text-base font-semibold">Daily Goal: {goals?.Goal || 5000}</div>
            <div className="text-sm"><span className="font-semibold">Goal Set Date :</span> {goalDate || 'MM/DD/YYYY'}</div>
            <div className="text-sm"><span className="font-semibold">Cumulative Balance :</span> {balance}</div>
          </div>

          {/* Daily Steps */}
          <div className="bg-white shadow-xl rounded-lg p-3">
            <h4 className="text-sm font-semibold mb-2">Daily Steps</h4>
            <div className="w-full">
              <table className="w-full text-xs border border-gray-300 table-fixed">
                <thead>
                  <tr className="bg-red-800 text-white">
                    <th className="p-1 text-left ">DAY</th>
                    <th className="p-1 text-left ">STEPS</th>
                    <th className="p-1 text-left ">ACTION</th>
                  </tr>
                </thead>
              </table>
              <div className="max-h-32 overflow-y-auto">
                <table className="w-full text-xs table-fixed">
                  <tbody>
                    {sortedDailySteps.map((item, idx) => (
                      <tr key={idx} className="bg-white border-b border-gray-300">
                        <td className="p-1 border border-gray-300">Day {item.day}</td>
                        <td className="p-1 border border-gray-300">{item.Steps}</td>
                        <td className="p-1 border border-gray-300 text-center">
                          <button
                            type="button"
                            className="text-black hover:text-blue-600 focus:outline-none text-base"
                            title="Edit"
                            onClick={() => {
                              setSelectedStep(item);
                              setShowEditModal(true);
                            }}
                          >
                            <i className="fa-solid fa-pencil"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>








            {showEditModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-lg w-[500px] p-4">
                  {/* <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold">Edit Steps</h2>
                    <button onClick={() => setShowEditModal(false)} className="text-red-600 font-bold">×</button>
                  </div> */}


                  <div className="bg-[#D9D9D9]  flex items-center justify-between px-4 py-2 border-b relative z-10">
                    <h2 className="text-lg font-semibold text-center w-full text-black">Edit Steps</h2>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="absolute right-3 text-white text-sm bg-[#870037] rounded-full w-5 h-5 flex items-center justify-center hover:bg-[#a00044] p-0"
                      // aria-label="Close"
                    >
                      ✕
                    </button>

                  </div>







                  <label className="text-sm font-semibold">Edit Steps</label>
                  <input
                    type="number"
                    className="w-full border p-2 rounded mt-1 mb-4"
                    value={selectedStep?.Steps || ''}
                    onChange={(e) =>
                      setSelectedStep({ ...selectedStep, Steps: e.target.value })
                    }
                  />
                  <div className="flex justify-between">
                    <button
                      className="bg-gray-500 text-white px-4 py-2 rounded"
                      onClick={() => setShowEditModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="bg-[#900028] text-white px-4 py-2 rounded"
                      onClick={() => {
                        // Save changes logic here
                        setShowEditModal(false);
                      }}
                    >
                      Submit
                    </button>
                  </div>
                   {/* <closeModal={handleClose} /> */}
                </div>
              </div>
            )}


            {/* Feedback */}
            <div className="mt-4">
              <h5 className="font-semibold text-sm mb-1">Feedback</h5>
              <div className="overflow-x-auto">
                <table className="min-w-full border border-blue-400 text-xs">
                  <thead>
                    <tr>
                      <th className="px-2 py-1 border text-left">Day</th>
                      {[1, 2, 3].map((num) => (
                        <th key={num} className="px-1 py-1 border text-left relative">
                          <span className="flex items-center gap-0.5">
                            {`Question ${num}`}
                            <span
                              className="text-red-800 cursor-pointer relative"
                              onMouseEnter={() => setHovered(num)}
                              onMouseLeave={() => setHovered(null)}
                            >
                              <InformationCircleIcon className="w-4 h-4 inline" />
                              {hovered === num && (
                                <div className="absolute left-0 top-5 bg-white text-gray-800 p-2 border border-gray-300 rounded shadow-lg text-xs w-48 z-50">
                                  {modalContent[num]}
                                </div>
                              )}
                            </span>
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFeedback.map((item, idx) => (
                      <tr key={idx}>
                        <td className="border px-2 py-1">{item.day}</td>
                        <td className="border px-2 py-1">{item.question1}</td>
                        <td className="border px-2 py-1">{item.question2}</td>
                        <td className="border px-2 py-1">{item.question3}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
          <div className="bg-white shadow-xl rounded-lg p-3 mb-4">
            <h6 className="font-semibold text-sm mb-1">Fresh Start Details   </h6>
            <h5 className="text-sm mb-1">Fresh Start Dates</h5>
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto border border-gray-400 text-xs">
                <tbody>
                  <tr>
                    {allDates.length > 0 ? (
                      allDates.map((item, index) => (
                        <td
                          key={index}
                          className="px-6 py-1 border border-gray-400 text-center font-semibold"
                        >
                          {item.date
                            ? new Date(item.date.seconds * 1000).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                            : 'Invalid Date'}
                        </td>
                      ))
                    ) : (
                      <td className="px-6 py-2 text-center text-gray-500" colSpan={5}>
                        No Fresh Start Used
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
      {loading && <LoadingSpinner />}
    </div>
  );
};

export default UserDetailsPage;
